import { Card, CardContent, Typography, Box } from "@mui/material";
import GenericTable from "../../Components/Back-End/Table";
import { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import SectionCard from "../../Components/Back-End/SectionCard";

export function FAQsTable() {
    const [validationErrors, setValidationErrors] = useState({});

    const columns = [
        {
            accessorKey: "id",
            header: "ID",
            enableEditing: false,
        },
        {
            accessorKey: "title",
            header: "Question",
            validate: (value) => {
                if (!value || value.trim() === "") return "Question is required.";
            },
            muiEditTextFieldProps: () => ({
                required: true,
                error: !!validationErrors.Title,
                helperText: validationErrors.Title ?? "",
                onChange: () =>
                    setValidationErrors((prev) => ({ ...prev, Title: undefined })),
            }),
        },
        {
            accessorKey: "description",
            header: "Answer",
            validate: (value) => {
                // Check empty or blank HTML (ReactQuill outputs "<p><br></p>" when empty)
                if (!value || value.trim() === "" || value === "<p><br></p>") {
                    return "Answer is required.";
                }
            },
            muiEditTextFieldProps: () => ({
                required: true,
                // We won't directly use error or helperText here because ReactQuill is a custom input,
                // but you still want to keep required for HTML form validation if applicable
            }),
            Edit: ({ cell, column, row, table }) => {
                const value = cell.getValue() || "";
                const error = table.options?.state?.validationErrors?.[column.id];

                const handleChange = (content, delta, source, editor) => {
                    row._valuesCache[column.id] = editor.getHTML();
                    table.setEditingRow(row);
                };

                return (
                    <Box sx={{ position: "relative" }}>
                        <Typography
                            sx={{ fontWeight: 300, marginTop: "0.5rem", fontSize: "0.8rem" }}
                        >
                            Answer
                        </Typography>

                        <Box
                            sx={{
                                border: error ? "1px solid red" : "1px solid #ccc",
                                borderRadius: 1,
                                "& .ql-container": {
                                    border: "none !important",
                                },
                            }}
                        >
                            <ReactQuill
                                theme="snow"
                                value={value}
                                onChange={handleChange}
                                modules={{
                                    toolbar: [
                                        ["bold", "underline"],
                                        [{ color: [] }, { background: [] }],
                                        ["link"],
                                        [{ list: "ordered" }, { list: "bullet" }],
                                    ],
                                }}
                                style={{ height: 250, marginBottom: "0.25rem" }}
                            />
                        </Box>

                        {error && (
                            <Typography variant="caption" color="error" sx={{ mt: 0 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                );
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();
                const truncated =
                    value.length > 50 ? value.substring(0, 50) + "..." : value;
                //console.log(truncated);
                return <div dangerouslySetInnerHTML={{ __html: truncated }} />;
            }
        },
        {
            accessorKey: "sortOrder",
            header: "Sort Order",
            meta: { hideInTable: true },
            muiEditTextFieldProps: () => ({
                type: "number",
                required: true,
                error: !!validationErrors.SortOrder,
                helperText: validationErrors.SortOrder ?? "",
                onChange: () =>
                    setValidationErrors((prev) => ({ ...prev, SortOrder: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Sort order is required.";
                }
                if (isNaN(value)) {
                    return "Sort order must be a number.";
                }
                if (Number(value) < 0) {
                    return "Sort order must be greater than or equal to 0.";
                }
            },
        },
        {
            accessorKey: "isActive",
            header: "Active",
            editVariant: "select",
            editSelectOptions: [
                { label: "Yes", value: true },
                { label: "No", value: false },
            ],
            muiEditTextFieldProps: () => ({
                select: true,
                required: true,
                defaultValue: true,
                error: !!validationErrors.IsActive,
                helperText: validationErrors.IsActive ?? "",
                onChange: () =>
                    setValidationErrors((prev) => ({ ...prev, IsActive: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Active status is required.";
                }
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? "Yes" : "No";
            },
        },
    ];

    const columnVisibility = columns.reduce((acc, col) => {
        if (col.meta?.hideInTable) {
            acc[col.accessorKey] = false;
        }
        return acc;
    }, {});

    return (
        <SectionCard title="FAQs" subtitle="Manage your FAQs below">
            <GenericTable
                columns={columns}
                itemName="FAQ"
                createApi="/api/faq/save"
                readApi="/api/faq/GetFAQs"
                updateApi="/api/faq/save"
                deleteApi="/api/faq/delete"
                columnVisibility={columnVisibility}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
                publicSitePage="aboutus"
            />
        </SectionCard>
    );
}
